import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO, eachDayOfInterval, startOfDay } from 'date-fns';
import {
  Trophy,
  TrendingUp,
  BarChart3,
  Target,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
} from 'recharts';
import { cn } from '@/lib/utils';
import {
  ABTest,
  ABTestVariant,
  useABTestAnalytics,
  calculateSignificance,
} from '@/hooks/useABTests';

interface ABTestAnalyticsProps {
  test: ABTest & { variants?: ABTestVariant[] };
  onDeclareWinner: (variantName: string) => void;
}

const VARIANT_COLORS = ['#667eea', '#f093fb', '#4facfe', '#43e97b', '#fa709a'];

export function ABTestAnalytics({ test, onDeclareWinner }: ABTestAnalyticsProps) {
  const { data: analyticsData, isLoading } = useABTestAnalytics(test.id);

  const variants = test.variants || [];
  const totalScans = variants.reduce((sum, v) => sum + v.scanCount, 0);

  // Calculate time series data
  const timeSeriesData = useMemo(() => {
    if (!analyticsData?.scans.length) return [];

    const scans = analyticsData.scans;
    const startDate = new Date(scans[0].scanned_at);
    const endDate = new Date(scans[scans.length - 1].scanned_at);

    const days = eachDayOfInterval({ start: startDate, end: endDate });

    return days.map((day) => {
      const dayStart = startOfDay(day);
      const dayData: Record<string, unknown> = { date: format(day, 'MMM d') };

      variants.forEach((v) => {
        const count = scans.filter(
          (s) =>
            s.ab_test_variant_id === v.id &&
            startOfDay(new Date(s.scanned_at)).getTime() === dayStart.getTime()
        ).length;
        dayData[`variant${v.variantName}`] = count;
      });

      return dayData;
    });
  }, [analyticsData, variants]);

  // Calculate significance between variants
  const significance = useMemo(() => {
    if (variants.length < 2) return null;

    const variantA = variants[0];
    const variantB = variants[1];

    return calculateSignificance(
      { scans: variantA.scanCount },
      { scans: variantB.scanCount }
    );
  }, [variants]);

  const leadingVariant = variants.reduce(
    (max, v) => (v.scanCount > (max?.scanCount || 0) ? v : max),
    variants[0]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BarChart3 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Scans</p>
                <p className="text-2xl font-bold">{totalScans}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Leading</p>
                <p className="text-2xl font-bold">Variant {leadingVariant?.variantName}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Variants</p>
                <p className="text-2xl font-bold">{variants.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'p-2 rounded-lg',
                  significance?.significant ? 'bg-green-500/10' : 'bg-yellow-500/10'
                )}
              >
                {significance?.significant ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Significance</p>
                <p className="text-2xl font-bold">
                  {significance?.confidence ? `${significance.confidence}%` : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Significance Banner */}
      {significance?.significant && test.status !== 'completed' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between p-4 rounded-lg bg-green-500/10 border border-green-500/20"
        >
          <div className="flex items-center gap-3">
            <Trophy className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-medium text-green-700">
                Statistical significance reached! ({significance.confidence}% confidence)
              </p>
              <p className="text-sm text-green-600">
                Variant {significance.winner} is outperforming with{' '}
                {leadingVariant?.scanCount} scans
              </p>
            </div>
          </div>
          <Button onClick={() => onDeclareWinner(significance.winner!)}>
            <Trophy className="h-4 w-4 mr-2" />
            Declare Winner
          </Button>
        </motion.div>
      )}

      {/* Time Series Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Scans Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          {timeSeriesData.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  {variants.map((v, i) => (
                    <Line
                      key={v.id}
                      type="monotone"
                      dataKey={`variant${v.variantName}`}
                      name={`Variant ${v.variantName}`}
                      stroke={VARIANT_COLORS[i % VARIANT_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
              <Info className="h-4 w-4 mr-2" />
              No scan data yet
            </div>
          )}
        </CardContent>
      </Card>

      {/* Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Variant Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Variant</TableHead>
                <TableHead className="text-right">Scans</TableHead>
                <TableHead className="text-right">Share</TableHead>
                <TableHead className="text-right">Avg/Day</TableHead>
                <TableHead>Performance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variants.map((variant, i) => {
                const percentage =
                  totalScans > 0 ? (variant.scanCount / totalScans) * 100 : 0;
                const isLeading = variant.id === leadingVariant?.id && totalScans > 0;
                const isWinner = test.winnerVariant === variant.variantName;
                const daysActive = test.startDate
                  ? Math.max(
                      1,
                      Math.ceil(
                        (Date.now() - new Date(test.startDate).getTime()) /
                          (1000 * 60 * 60 * 24)
                      )
                    )
                  : 1;
                const avgPerDay = variant.scanCount / daysActive;

                return (
                  <TableRow key={variant.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length] }}
                        />
                        <span className="font-medium">Variant {variant.variantName}</span>
                        {isWinner && (
                          <Badge variant="default" className="text-xs">
                            <Trophy className="h-3 w-3 mr-1" />
                            Winner
                          </Badge>
                        )}
                        {!isWinner && isLeading && (
                          <Badge variant="secondary" className="text-xs">
                            Leading
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {variant.scanCount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">{percentage.toFixed(1)}%</TableCell>
                    <TableCell className="text-right font-mono">
                      {avgPerDay.toFixed(1)}
                    </TableCell>
                    <TableCell>
                      <Progress
                        value={percentage}
                        className={cn(
                          'h-2 w-24',
                          isWinner && '[&>div]:bg-green-500'
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Bar Chart Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Total Scans Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={variants.map((v, i) => ({
                  name: `Variant ${v.variantName}`,
                  scans: v.scanCount,
                  fill: VARIANT_COLORS[i % VARIANT_COLORS.length],
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="name" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="scans" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
